/* The slice of Smartico's browser SDK this app uses.

   Declared here rather than pulled from `@smartico/public-api`, for the same
   reason BetBy's `window.BTRenderer` is declared inside the sportsbook
   component: we call four methods on a script we do not bundle, and a
   dependency whose types we would use and whose code we would not is weight on
   every build for no runtime gain.

   The trade is that these shapes are ours to keep true. They are taken from the
   whitelabel's own integration and from the vendor's published types; anything
   we do not call is deliberately absent. */

/* A mission as Smartico sends it. Only the fields the widget reads are here —
   the real payload is much wider (rewards, tags, categories, badges).

   `dt_start` is seconds in some payloads and milliseconds in others, which is
   why every timestamp goes through `toMilliseconds` before it is used. */
export type SmarticoMission = {
  id: number;
  name: string;
  image: string;
  /* 0–100, computed by Smartico. */
  progress: number;
  /* The same feed carries badges; only `mission` belongs in this widget. */
  type: string;
  position?: number;
  is_completed?: boolean;
  is_locked?: boolean;
  is_requires_optin?: boolean;
  is_opted_in?: boolean;
  /* The absolute window the mission is offered in. 0 means unbounded. */
  active_from_ts?: number;
  active_till_ts?: number;
  /* A per-player clock that starts when they opt in, rather than a fixed date. */
  time_limit_ms?: number;
  dt_start?: number;
  tasks?: SmarticoTask[];
};

/* `display_progress_as_count` decides whether the bar reads "3/10" or "30%".
   It is absent from the vendor's own published type, so it is optional here and
   read defensively — same as the whitelabel does. */
export type SmarticoTask = {
  execution_count_expected?: number;
  execution_count_actual?: number;
  display_progress_as_count?: boolean;
};

/* A level. `required_points` is the XP floor for entering it. */
export type SmarticoLevel = {
  id: number;
  name: string;
  image: string;
  ordinal_position: number;
  required_points: number;
};

/* The player's gamification profile. `ach_points_ever` is XP — the lifetime
   points total the level ladder is measured against, as opposed to
   `ach_points_balance`, which is what is left after spending. */
export type SmarticoUserProfile = {
  ach_points_ever?: number;
  ach_points_balance?: number;
  ach_level_current?: string | number;
};

export type SmarticoApi = {
  getMissions: (options?: { onUpdate?: (missions: SmarticoMission[]) => void }) => Promise<
    SmarticoMission[]
  >;
  getLevels: () => Promise<SmarticoLevel[]>;
  getUserProfile: () => SmarticoUserProfile;
};

/* The events we subscribe to. `props_change` fires when the profile moves —
   which is how XP updates without polling — and `gf_closing` when the player
   shuts Smartico's own dashboard, where they may have just claimed something. */
export type SmarticoEvent = "props_change" | "gf_closing";

export type SmarticoGlobal = {
  api?: SmarticoApi;
  init: (labelKey: string, options: { brand_key: string }) => void;
  online: (userId: string) => void;
  logout: () => void;
  /* Smartico can render its own inbox and popups over the page. Both are
     suspended on load — see SmarticoProvider. */
  suspendInbox: (flag: boolean) => void;
  suspendPopups: (flag: boolean) => void;
  /* Deep links into Smartico's own dashboard: "dp:gf_missions", "dp:gf_levels". */
  dp: (deepLink: string) => void;
  on: (event: string, handler: (...args: never[]) => void) => void;
  off: (event: string, handler: (...args: never[]) => void) => void;
};

declare global {
  interface Window {
    _smartico?: SmarticoGlobal;
    _smartico_user_id?: string | null;
    _smartico_language?: string | null;
    _smartico_allow_localhost?: boolean;
  }
}

/* Smartico sends timestamps in seconds or milliseconds depending on the field
   and the payload. Anything below ~2001 in milliseconds is really seconds. */
export const toMilliseconds = (timestamp: number) =>
  timestamp > 0 && timestamp < 1e12 ? timestamp * 1000 : timestamp;
